import axios from "axios";
import { quizSessionToken } from '../utils/quizSession';
import Swal from "sweetalert2";
import {
  clearAllAuthStorage,
  reconcileTokenOrigin,
  stampTokenOrigin,
  TEACHER_TOKEN_KEY,
  STUDENT_TOKEN_KEY,
  TOKEN_ORIGIN_KEY,
} from '../utils/localStorageKeys';

  // Auto-select base URL:
  // - If VITE_API_BASE_URL is set (recommended), use it.
  // - Else if running production build (import.meta.env.PROD), use PROD_BASE_URL.
  // - Otherwise use local URL.
  export const PROD_BASE_URL = "https://test.unchay.in/";
  export const LOCAL_BASE_URL = "http://localhost:8000/";

  const envBase = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_BASE_URL;
  export const baseURL = envBase ?? (import.meta.env.PROD ? PROD_BASE_URL : LOCAL_BASE_URL);

  // Boot reconcile: if the stored token was minted against a DIFFERENT backend
  // (local↔prod), it will 401 here → clear it silently now so the user lands on a
  // clean login instead of the flash-/myprofile → hard-reload bounce mid-session.
  reconcileTokenOrigin(baseURL);

  export const API = axios.create({
    baseURL,
  });

// Helper to safely parse tenant from localStorage
const parseTenantData = (tenantString) => {
  if (!tenantString) return null;
  try {
    const data = JSON.parse(tenantString);
    // Validate that the parsed data has a valid tenant ID
    const tenantId = data?._id || data?.id;
    if (tenantId && typeof tenantId === 'string' && tenantId.length > 0) {
      return { data, tenantId };
    }
    return null;
  } catch {
    return null;
  }
};

// Debug logging for API requests (removed for production)
const logApiRequest = () => {};

// Request interceptor to add the token and tenant header
API.interceptors.request.use(
  (config) => {
    // Check for teacher token first, then student token.
    const teacherToken = localStorage.getItem('token');
    const studentToken = localStorage.getItem('studentToken');
    // Student-app endpoints (student JWT required) opt in via `useStudentToken`
    // so a lingering teacher/super-admin session in the SAME browser doesn't
    // shadow them — the student routes reject non-student tokens with 403.
    // Note: some /student/* routes are teacher-authenticated (class-wise, add,
    // bulk-upload), so we can't blanket-prefer the student token by path.
    const token = config.useStudentToken
      ? (studentToken || teacherToken)
      : (teacherToken || studentToken);

    const tenant = localStorage.getItem('tenant');
    const managingTenant = localStorage.getItem('managingTenant');

    if (token) {
      // Clean the token - remove quotes if present
      const cleanToken = token.replace(/^"(.*)"$/, '$1');
      config.headers['Authorization'] = `Bearer ${cleanToken}`;
    }

    // For super admin cross-tenant management: use managingTenant if valid
    // Otherwise fall back to user's tenant
    const managingTenantParsed = parseTenantData(managingTenant);
    const tenantParsed = parseTenantData(tenant);

    // Priority: managingTenant > user's tenant
    const activeTenant = managingTenantParsed || tenantParsed;

    // Add tenant ID header if we have a valid tenant
    if (activeTenant) {
      config.headers['X-Tenant-ID'] = activeTenant.tenantId;
    }

    // Log request details in dev mode for debugging
    logApiRequest(config, {
      hasToken: !!token,
      tokenType: teacherToken ? 'teacher' : (studentToken ? 'student' : 'none'),
      hasTenant: !!activeTenant,
      tenantId: activeTenant?.tenantId?.substring(0, 8)
    });

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

API.interceptors.response.use(
  (response) => {
    // Pick up auto-extended token from server. Update the SAME slot that was
    // used for this request (keyed by the request's useStudentToken flag) so a
    // teacher with a lingering studentToken in localStorage doesn't silently
    // receive their extended teacher JWT in the wrong slot and miss extensions.
    const newToken = response.headers['x-new-token'];
    if (newToken) {
      if (response.config?.useStudentToken && localStorage.getItem(STUDENT_TOKEN_KEY)) {
        localStorage.setItem(STUDENT_TOKEN_KEY, newToken);
      } else {
        localStorage.setItem(TEACHER_TOKEN_KEY, newToken);
      }
    }
    // Stamp the origin this (now-validated) token belongs to, so a later
    // environment switch can detect the mismatch on boot. Cheap, only on change.
    if (localStorage.getItem(TEACHER_TOKEN_KEY) || localStorage.getItem(STUDENT_TOKEN_KEY)) {
      if (localStorage.getItem(TOKEN_ORIGIN_KEY) !== baseURL) stampTokenOrigin(baseURL);
    }
    return response;
  },
  async (error) => {
    // Handle daily usage limit reached
    if (error.response?.status === 429 && error.response?.data?.code === 'DAILY_LIMIT_REACHED') {
      Swal.fire({
        title: 'Daily Limit Reached',
        text: error.response.data.message,
        icon: 'warning',
        confirmButtonColor: '#ff9800',
      });
      return Promise.reject(error);
    }

    if (error.response && error.response.status === 401) {
      // Public funnel endpoints (`/generate`) intentionally return 401
      // (GUEST_GEN_LOGIN_REQUIRED — the hard 4th-attempt wall) to a logged-OUT
      // visitor. That is NOT a session-expiry signal, so we must NOT clear auth
      // or redirect to '/'. Opt these calls out via `config.skipAuthRedirect`
      // and let the caller open the signup wall instead.
      if (error.config?.skipAuthRedirect) {
        return Promise.reject(error);
      }
      // Clear ALL auth keys (teacher, student, tenant) for complete logout, then
      // do a SOFT logout: a non-blocking toast + a React-Router redirect (via the
      // SessionExpiryListener) instead of the old jarring `window.location.href='/'`
      // full-page reload that made expiry "loud" during demos.
      clearAllAuthStorage();
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'info',
        title: 'Session expired — please sign in again',
        showConfirmButton: false,
        timer: 3000,
      });
      window.dispatchEvent(new CustomEvent('auth:session-expired'));
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

  // Demo Request (public landing-page lead capture) //
  export const submitDemoRequest = (data) => API.post("/demo-request", data);

  // Demo Request — super-admin lead management //
  export const getDemoRequests = (params = {}) => API.get("/admin/demo-request", { params });
  export const getOpenLeadsCount = () => API.get("/admin/demo-request/count");
  export const updateDemoRequest = (id, payload) => API.patch(`/admin/demo-request/${id}`, payload);
  export const addDemoRequestContactLog = (id, payload) =>
    API.post(`/admin/demo-request/${id}/contact-log`, payload);

  // Evaluation Overview — super-admin, across every institute //
  // PRD-SUPER-ADMIN-EVALUATION-OVERVIEW. Read-only in phase 1.
  export const getEvaluationOverviewSummary = () => API.get("/admin/evaluations/summary");
  export const getEvaluationOverviewAssessments = (params = {}) =>
    API.get("/admin/evaluations/assessments", { params });
  export const getEvaluationOverviewPapers = (tenantId, assessmentId, params = {}) =>
    API.get(`/admin/evaluations/assessments/${tenantId}/${assessmentId}/papers`, { params });
  // View-only: one institute's question paper, and one student's answer sheet
  // with the ticks drawn on (2026-10-29).
  export const getEvaluationOverviewPaper = (tenantId, assessmentId) =>
    API.get(`/admin/evaluations/assessments/${tenantId}/${assessmentId}/paper`);
  export const getEvaluationOverviewSheet = (tenantId, evaluationId) =>
    API.get(`/admin/evaluations/papers/${tenantId}/${evaluationId}/sheet`);
  // Parent share links for an institute's papers, made by the super admin. The
  // institute comes from the URL, not from the caller's login — the teacher's
  // own `/parent-report/generate-bulk` cannot serve cross-tenant mode.
  export const generateEvaluationOverviewParentLinks = (tenantId, assessmentId, evaluationIds = []) =>
    API.post(`/admin/evaluations/assessments/${tenantId}/${assessmentId}/parent-links`, { evaluationIds });

  // Tenant API Start //
  export const getActiveTenants = () => API.get("/tenant/active");
  export const getAllTenants = () => API.get("/tenant/all");
  export const getTenantById = (tenantId) => API.get(`/tenant/${tenantId}`);
  export const createTenant = (tenantDetails) => API.post("/tenant/create", tenantDetails);
  export const updateTenant = (tenantId, tenantDetails) => API.put(`/tenant/${tenantId}`, tenantDetails);
  export const deactivateTenant = (tenantId) => API.put(`/tenant/${tenantId}/deactivate`);
  export const activateTenant = (tenantId) => API.put(`/tenant/${tenantId}/activate`);
  export const getTenantStats = () => API.get("/tenant/stats");
  // Super Admin - Tenant User Management
  export const getTenantUsers = (tenantId) => API.get(`/tenant/${tenantId}/users`);
  export const createTenantUser = (tenantId, userData) => API.post(`/tenant/${tenantId}/users`, userData);
  export const updateTenantUserRole = (tenantId, userId, roleData) => API.put(`/tenant/${tenantId}/users/${userId}/role`, roleData);
  export const updateTenantUserStatus = (tenantId, userId, statusData) => API.put(`/tenant/${tenantId}/users/${userId}/status`, statusData);
  export const updateTenantUserProfile = (tenantId, userId, data) => API.put(`/tenant/${tenantId}/users/${userId}/profile`, data);
  // Tenant API End //

  //Teacher Auth Start//
  // Helper to create config with tenant header
  const withTenantHeader = (tenantId) => {
    if (tenantId) {
      return { headers: { 'X-Tenant-ID': tenantId } };
    }
    return {};
  };

  export const signup = (userdetails) =>
    API.post("/user/signup", userdetails, withTenantHeader(userdetails.tenantId));
  export const googlesignup = (userdetails) =>
    API.post("/user/googlesignup", userdetails, withTenantHeader(userdetails.tenantId));
  export const signin = (userdetails) =>
    API.post("/user/signin", userdetails, withTenantHeader(userdetails.tenantId));
  export const forgotpassword = (userdetails) => API.post("/user/forgotpassword", userdetails);
  export const resetpassword = (userdetails) => API.post("/user/resetpassword", userdetails);
  export const changeTempPassword = (passwordData) => API.post("/user/change-temp-password", passwordData);
  export const linkGoogleAccount = (data) =>
    API.post("/user/link-google", data, withTenantHeader(data.tenantId));
  export const logout = (userdetails) => API.post("/user/logout", userdetails);
  //Teacher Auth End//

  // ============ Independent Teacher API Start ============
  export const independentSignin = (data) => API.post('/independent/signin', data);
  export const independentSignup = (data) => API.post('/independent/signup', data);
  export const independentGoogleSignup = (data) => API.post('/independent/googlesignup', data);
  export const independentLinkGoogle = (data) => API.post('/independent/link-google', data);
  export const independentLogout = (data) => API.post('/independent/logout', data);
  export const independentForgotPassword = (data) => API.post('/independent/forgotpassword', data);
  export const independentResetPassword = (data) => API.post('/independent/resetpassword', data);
  export const getIndependentUsage = () => API.get('/independent/usage');
  // Public — daily-allowance display values for the unauthenticated login page.
  // Reads from the same Tenant.dailyLimits doc UsageLimitService enforces
  // against, so what we advertise = what we enforce.
  export const getFreeTierLimits = () => API.get('/independent/free-tier-limits');
  // ============ Independent Teacher API End ============

  // Institute Admin - User Management API Start //
  export const getAllUsers = () => API.get("/user/all");
  export const getUserStats = () => API.get("/user/stats");
  export const createUser = (userDetails) => API.post("/user/create", userDetails);
  export const updateUserStatus = (userId, statusData) => API.put(`/user/${userId}/status`, statusData);
  export const updateUserRole = (userId, roleData) => API.put(`/user/${userId}/role`, roleData);
  export const deleteUser = (userId) => API.delete(`/user/${userId}`);
  // Institute Admin - User Management API End //

  //Assessment Start//
  export const extractQuestions = (assignmentdetails) => API.post("/assessment/selfassessment", assignmentdetails);
  export const createQuestions = (questiondetails) => API.post("/questions/createQuestions", questiondetails);
  export const replaceQuestion = (questiondetails) => API.post("/questions/replaceQuestions", questiondetails);
  export const replaceAnswer = (questiondetails) => API.post("/questions/replaceAnswer", questiondetails);
  export const createAssessment = (assignmentdetails) => API.post("/assessment/newassessment", assignmentdetails);
  // Extract plain text from an uploaded material file (.txt/.pdf/.docx) for the
  // Quiz AI "generate from my material" mode. Expects a FormData with `file`.
  export const extractMaterial = (formData) =>
    API.post("/assessment/extract-material", formData, { headers: { 'Content-Type': 'multipart/form-data' } });
// Assessment Generation Status (Async polling)
export const getGenerationStatus = (jobId) => API.get(`/assessment/generation-status/${jobId}`);
export const retryFailedTopics = (jobId) => API.post(`/assessment/generation-retry/${jobId}`);
// Rubric Management (assessment routes)
export const generateRubrics = (assessmentId) =>
  API.post(`/assessment/${assessmentId}/rubrics/generate`);
export const getRubrics = (assessmentId) =>
  API.get(`/assessment/${assessmentId}/rubrics`);
export const updateRubric = (rubricId, data) =>
  API.put(`/assessment/rubrics/${rubricId}`, data);
export const approveRubrics = (assessmentId) =>
  API.post(`/assessment/${assessmentId}/rubrics/approve`);
// Atom rubric overrides (Manage Rubrics — PRD-MANAGE-RUBRICS-ATOMS)
export const saveRubricAtomOverride = (assessmentId, shape, atoms) =>
  API.put(`/assessment/${assessmentId}/rubrics/atoms`, { shape, atoms });
export const getRubricAtomLibrary = () =>
  API.get(`/assessment/rubrics/atom-library`);
// Question Review & Draft (Phase 2)
export const regenerateQuestion = (data) => API.post('/assessment/regenerate-question', data);
export const saveDraft = (data) => API.post('/assessment/save-draft', data);
export const finalizeAssessment = (data) => API.post('/assessment/finalize', data);

// Review assessment with questions and performance data
export const reviewAssessment = (assessmentId) => API.post(`/assessment/review/${assessmentId}`);

// Question Types (dynamic picker)
export const getQuestionTypes = (params) => {
  const queryParams = new URLSearchParams();
  if (params.grade) queryParams.append('grade', params.grade);
  if (params.subject) queryParams.append('subject', params.subject);
  // A college paper sends its level instead of a grade (2026-10-21).
  if (params.level) queryParams.append('level', params.level);
  return API.get(`/assessment/question-types?${queryParams.toString()}`);
};

// Blueprint Templates
export const getBlueprints = (params) => {
  const queryParams = new URLSearchParams();
  if (params.board) queryParams.append('board', params.board);
  if (params.grade) queryParams.append('grade', params.grade);
  if (params.subject) queryParams.append('subject', params.subject);
  if (params.assessmentCategory) queryParams.append('assessmentCategory', params.assessmentCategory);
  return API.get(`/assessment/blueprints?${queryParams.toString()}`);
};
export const getBlueprintById = (id) => API.get(`/assessment/blueprints/${id}`);
export const getBlueprintCategories = (params) => {
  const queryParams = new URLSearchParams();
  if (params.board) queryParams.append('board', params.board);
  if (params.grade) queryParams.append('grade', params.grade);
  if (params.subject) queryParams.append('subject', params.subject);
  return API.get(`/assessment/blueprints/categories?${queryParams.toString()}`);
};
export const createBlueprintTemplate = (data) => API.post('/assessment/blueprints', data);
export const updateBlueprintTemplate = (id, data) => API.put(`/assessment/blueprints/${id}`, data);
export const deleteBlueprintTemplate = (id) => API.delete(`/assessment/blueprints/${id}`);

// Quiz finalize hub — download a PDF of the assessment (Qs + answer key)
// and publish the assessment for online access.
export const downloadAssessmentPdf = (assessmentId) =>
  API.get(`/assessment/${assessmentId}/download-pdf`);
export const publishAssessmentOnline = (assessmentId) =>
  API.patch(`/assessment/${assessmentId}/publish-online`);
//Assessment End//

//Evaluation Start//
export const getuserAssessments = (assignmentdetails) => API.post("/assessment/getAssessments", assignmentdetails);
export const retireAssessments = (assessmentIds) => API.patch("/assessment/retire", { assessmentIds });
export const getAssessmentsById = (assignmentdetails) => API.post("/assessment/getAssessmentByid", assignmentdetails);
// Edit the paper's section structure ("attempt any N of M"). Server validates
// in BLOCK mode and 400s with the violated invariant (PRD-PAPER-STRUCTURE L3).
export const updateAssessmentStructure = (assessmentId, sections) =>
  API.put(`/assessment/${assessmentId}/structure`, { sections });
// Rename a paper. `source` records which screen it was renamed from
// ('profile' | 'assessments') in the paper's rename history.
export const renameAssessment = (assessmentId, name, source) =>
  API.patch(`/assessment/${assessmentId}/name`, { name, source });
export const checkAnswersheet = (evaluationdetails) => API.post("/evaluation/checkAnswer", evaluationdetails);
export const studentReport = (evaluationdetails) => API.get("/evaluation/studentReport", evaluationdetails);
export const viewEvaluations = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.assessmentId) queryParams.append('assessmentId', params.assessmentId);
  if (params.startDate) queryParams.append('startDate', params.startDate);
  if (params.endDate) queryParams.append('endDate', params.endDate);
  if (params.needsReview) queryParams.append('needsReview', params.needsReview);
  return API.get(`/evaluation/viewEvaluations?${queryParams.toString()}`);
};

export const downloadReport = (evaluationId) =>
  API.get(`/evaluation/download-report/${evaluationId}?lang=${localStorage.getItem('mz_lang') || 'en'}`, { responseType: 'blob' });

export const getQuestionAnalytics = (assessmentId) =>
  API.get(`/evaluation/${assessmentId}/question-analytics`);

// Cross-assessment ("question bank") difficulty analytics. Backend decides scope
// from the caller's role: a teacher gets their own questions, an admin/principal
// gets every teacher's in the tenant.
export const getQuestionBankAnalytics = () =>
  API.get(`/evaluation/question-bank-analytics`);

export const downloadAllReportsZip = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.assessmentId) queryParams.append('assessmentId', params.assessmentId);
  if (params.evaluationIds) queryParams.append('evaluationIds', params.evaluationIds);
  return API.get(`/evaluation/download-all?${queryParams.toString()}`, { responseType: 'blob' });
};

// Bulk Evaluation
export const startBulkEvaluation = (formData) => API.post("/evaluation/bulk", formData, {
  headers: { "Content-Type": "multipart/form-data" }
});
export const getBulkEvaluationStatus = (jobId) => API.get(`/evaluation/bulk/${jobId}/status`);
export const getBulkEvaluationResults = (jobId) => API.get(`/evaluation/bulk/${jobId}/results`);
// Upload caps, served rather than hardcoded — when the client's copy drifted
// from the server's, the panel accepted batches multer rejected at the door
// after the upload had already run (2026-07-29).
export const getBulkEvaluationLimits = () => API.get('/evaluation/bulk/limits');
// Best-effort client-side lifecycle logging for the bulk-evaluation UI, forwarded
// into the server's Winston log so it can be read alongside BULK_* server events.
// See docs/BUG-FIX-LOG.md 2026-08-03.
export const logBulkClientEvent = (data) => API.post('/evaluation/bulk/client-event', data);

// Per-student bulk pipeline — pages stream to S3 one student at a time and each
// student grades on arrival, instead of one multipart request for the whole
// batch. See docs/PLAN-BULK-EVALUATION-REBUILD.md §3.2.
export const openBulkJob = (assessment) => API.post('/evaluation/bulk/job', { assessment });
export const registerBulkStudent = (jobId, files, name) => {
  const formData = new FormData();
  files.forEach((file) => formData.append('pages', file));
  if (name) formData.append('name', name);
  return API.post(`/evaluation/bulk/job/${jobId}/student`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const commitBulkStudent = (jobId, studentSerial) =>
  API.post(`/evaluation/bulk/job/${jobId}/student/${studentSerial}/commit`);
export const retryBulkStudent = (jobId, studentSerial) =>
  API.post(`/evaluation/bulk/job/${jobId}/student/${studentSerial}/retry`);
export const closeBulkJob = (jobId) => API.post(`/evaluation/bulk/job/${jobId}/close`);

// Scanner-folder ingest — docs/PRD-BULK-SCANNER-INGEST.md §8. The folder is
// uploaded in chunks; every page is transcribed once; identity is read from the
// transcripts; the teacher confirms the sort; grading uses the normal job.
export const openScannerJob = (body) => API.post('/evaluation/bulk/scanner/job', body);
export const uploadScannerPages = (jobId, files) => {
  const formData = new FormData();
  files.forEach((file) => formData.append('pages', file, file.scannerName || file.name));
  return API.post(`/evaluation/bulk/scanner/${jobId}/pages`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const closeScannerPages = (jobId, expectedSources) => API.post(`/evaluation/bulk/scanner/${jobId}/pages/close`, { expectedSources });
export const setScannerPageStatus = (jobId, seq, status) => API.patch(`/evaluation/bulk/scanner/${jobId}/pages/${seq}`, { status });
export const getScannerSort = (jobId) => API.get(`/evaluation/bulk/scanner/${jobId}/sort`);
export const patchScannerSort = (jobId, body) => API.patch(`/evaluation/bulk/scanner/${jobId}/sort`, body);
export const confirmScannerSort = (jobId, body) => API.post(`/evaluation/bulk/scanner/${jobId}/sort/confirm`, body);
export const resumeScannerJob = (jobId, body = {}) => API.post(`/evaluation/bulk/scanner/${jobId}/resume`, body);
export const gradeScannerHeldAnyway = (jobId, roll) => API.post(`/evaluation/bulk/scanner/${jobId}/held/${encodeURIComponent(roll)}/grade-anyway`);
export const listOpenScannerJobs = (assessment) => API.get('/evaluation/bulk/scanner/open', { params: assessment ? { assessment } : {} });

// Answer Sheet Validation
export const validateAnswerSheet = (formData) => API.post("/evaluation/validate", formData, {
  headers: { "Content-Type": "multipart/form-data" }
});
export const validateBulkAnswerSheets = (formData) => API.post("/evaluation/validate-bulk", formData, {
  headers: { "Content-Type": "multipart/form-data" }
});

// Evaluation detail & Re-evaluation
export const getEvaluationDetail = (evaluationId) =>
  API.get(`/evaluation/detail/${evaluationId}?lang=${localStorage.getItem('mz_lang') || 'en'}`);
export const reEvaluateQuestion = (evaluationId, data) =>
  API.post(`/evaluation/re-evaluate/${evaluationId}`, data);
// ADMIN ONLY — every retired/held copy of one report card. The server returns
// 403 for anyone else; the caller also guards so the request is never fired.
export const getEvaluationVersions = (evaluationId) =>
  API.get(`/evaluation/${evaluationId}/versions`);

// Phase 3B — Grading workflow & score override
export const overrideQuestionScore = (evaluationId, qIndex, data) =>
  API.put(`/evaluation/${evaluationId}/question/${qIndex}/override`, data);
export const approveQuestion = (evaluationId, qIndex) =>
  API.put(`/evaluation/${evaluationId}/question/${qIndex}/approve`);
export const bulkApproveEvaluation = (evaluationId) =>
  API.put(`/evaluation/${evaluationId}/approve`);
export const editAnswerKey = (evaluationId, data) =>
  API.put(`/evaluation/${evaluationId}/answer-key`, data);
export const disapproveQuestion = (evaluationId, qIndex, data) =>
  API.put(`/evaluation/${evaluationId}/question/${qIndex}/disapprove`, data);

// #5 — Editable report card (human-in-the-loop). Each edit appends audit
// history server-side; `reason` is optional. Score edit recomputes the total.
export const editQuestionAnswer = (evaluationId, qIndex, data) =>
  API.put(`/evaluation/${evaluationId}/question/${qIndex}/answer`, data);
export const editQuestionFeedback = (evaluationId, qIndex, data) =>
  API.put(`/evaluation/${evaluationId}/question/${qIndex}/feedback`, data);
export const editQuestionScore = (evaluationId, qIndex, data) =>
  API.put(`/evaluation/${evaluationId}/question/${qIndex}/score`, data);

// Correct a misread student name/roll number (OCR/LLM extraction error).
// Root-level, not per-question — no re-grading. `reason` is optional.
export const editStudentIdentity = (evaluationId, data) =>
  API.put(`/evaluation/${evaluationId}/identity`, data);

// Scan-evaluation API methods moved to ./scanApi.js — they're used only by lazy
// scan/teacher-app routes, so keeping them out of this entry-chunk module
// protects the landing size-limit budget. Import them from '../api/scanApi'.

// Extraction Lab — v1 endpoints (legacy)
export const extractionLabAnalyze = (formData) =>
  API.post('/admin/extraction-lab/analyze', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const extractionLabEvaluate = (data) =>
  API.post('/admin/extraction-lab/evaluate', data);
export const extractionLabCompareFiles = (formData) =>
  API.post('/admin/extraction-lab/compare-files', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

// Extraction Lab — v2 session endpoints
export const extractionLabCreateSession = (formData) =>
  API.post('/admin/extraction-lab/sessions', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const extractionLabGetSession = (sessionId) =>
  API.get(`/admin/extraction-lab/sessions/${sessionId}`);
export const extractionLabDeleteSession = (sessionId) =>
  API.delete(`/admin/extraction-lab/sessions/${sessionId}`);
export const extractionLabRunExtraction = (sessionId, data) =>
  API.post(`/admin/extraction-lab/sessions/${sessionId}/extract`, data);
export const extractionLabGetExtractionPrompt = (sessionId) =>
  API.get(`/admin/extraction-lab/sessions/${sessionId}/prompts/extraction`);
export const extractionLabRunMapping = (sessionId, data) =>
  API.post(`/admin/extraction-lab/sessions/${sessionId}/map-answers`, data);
export const extractionLabGetMappingPrompt = (sessionId) =>
  API.get(`/admin/extraction-lab/sessions/${sessionId}/prompts/mapping`);
export const extractionLabRunComparison = (sessionId, data) =>
  API.post(`/admin/extraction-lab/sessions/${sessionId}/compare`, data);
export const extractionLabGetComparisonPrompt = (sessionId, params) =>
  API.get(`/admin/extraction-lab/sessions/${sessionId}/prompts/comparison`, { params });
export const extractionLabGetSummary = (sessionId) =>
  API.get(`/admin/extraction-lab/sessions/${sessionId}/summary`);
export const extractionLabGetModels = () =>
  API.get('/admin/extraction-lab/models');

// Extraction Lab — prompt management
export const extractionLabSavePrompt = (data) =>
  API.post('/admin/extraction-lab/prompts/save', data);
export const extractionLabGetPromptHistory = (params) =>
  API.get('/admin/extraction-lab/prompts/history', { params });
export const extractionLabActivatePrompt = (promptId) =>
  API.put(`/admin/extraction-lab/prompts/${promptId}/activate`);
export const extractionLabExportPrompt = (promptId) =>
  API.get(`/admin/extraction-lab/prompts/${promptId}/export`);

// Model Routing Rules (Super Admin)
export const getModelRules = () =>
  API.get('/admin/model-mappings');
export const saveModelRule = (data) =>
  API.post('/admin/model-mappings', data);
export const deleteModelRule = (activity, subjectType) =>
  API.delete(`/admin/model-mappings/${activity}/${subjectType || 'null'}`);
export const seedModelDefaults = () =>
  API.post('/admin/model-mappings/seed');
export const getAvailableModels = () =>
  API.get('/admin/model-mappings/available-models');

// Platform switches — flipped without a restart (owner only to change)
export const getPlatformSettings = () =>
  API.get('/admin/platform-settings');
export const savePlatformSetting = (key, value) =>
  API.put(`/admin/platform-settings/${key}`, { value });

//View online assessment result
export const viewresult= (onlineassessmentId) => API.get(`/online/viewresult?onlineAssessmentId=${onlineassessmentId}`);

// Chapters and Topics API Start //
/**
 * Fetch chapters based on country, board, grade, and subject
 * @param {Object} params - { country, board, grade, subject }
 * @returns {Promise} - Array of chapter names
 */
export const getChapters = (params) => {
  const { country, board, grade, subject } = params;
  return API.get(`/chapters?Country=${country}&Board=${board}&Grade=${grade}&L1_Subject=${subject}`);
};

/**
 * Fetch topics for a specific chapter
 * @param {Object} params - { country, board, grade, subject, chapter }
 * @returns {Promise} - Array of topic names
 */
export const getTopics = (params) => {
  const { country, board, grade, subject, chapter } = params;
  return API.get(`/topics?Country=${country}&Board=${board}&Grade=${grade}&L1_Subject=${subject}&ChapterName=${chapter}`);
};

/**
 * Fetch topics for multiple chapters (batch request)
 * @param {Object} params - { country, board, grade, subject, chapters: string[] }
 * @returns {Promise} - Array of all unique topics
 */
export const getTopicsForChapters = async (params) => {
  const { country, board, grade, subject, chapters } = params;

  // Create promises for each chapter
  const topicPromises = chapters.map(chapter =>
    API.get(`/topics?Country=${country}&Board=${board}&Grade=${grade}&L1_Subject=${subject}&ChapterName=${chapter}`)
  );

  // Wait for all promises to resolve
  const responses = await Promise.all(topicPromises);

  // Combine all topics arrays and remove duplicates
  const allTopics = Array.from(new Set(responses.flatMap(response => response.data)));

  return { data: allTopics };
};
// Chapters and Topics API End //

// ============ Teacher Management API Start ============

// Admin - Invitation Management
export const createTeacherInvitation = (invitationData) =>
  API.post("/teacher/invite", invitationData);

export const bulkInviteTeachers = (teachersData) =>
  API.post("/teacher/invite/bulk", teachersData);

export const resendTeacherInvitation = (invitationId) =>
  API.post(`/teacher/invite/${invitationId}/resend`);

export const cancelTeacherInvitation = (invitationId) =>
  API.put(`/teacher/invite/${invitationId}/cancel`);

export const getAllInvitations = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.status) queryParams.append('status', params.status);
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.search) queryParams.append('search', params.search);
  return API.get(`/teacher/invitations?${queryParams.toString()}`);
};

// Admin - Teacher Management
export const getAllTeachers = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.status) queryParams.append('status', params.status);
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.search) queryParams.append('search', params.search);
  return API.get(`/teacher/all?${queryParams.toString()}`);
};

export const updateTeacherDetails = (teacherId, teacherData) =>
  API.put(`/teacher/${teacherId}`, teacherData);

// Admin - Statistics
export const getTeacherManagementStats = () =>
  API.get("/teacher/stats");

// Public - Invitation Acceptance Flow
export const validateInviteToken = (token, tenantId) => {
  const config = tenantId ? { headers: { 'X-Tenant-ID': tenantId } } : {};
  return API.get(`/teacher/invite/validate?token=${token}`, config);
};

export const sendInvitationOTP = (data, tenantId) => {
  const config = tenantId ? { headers: { 'X-Tenant-ID': tenantId } } : {};
  return API.post("/teacher/invite/send-otp", data, config);
};

export const verifyOTPAndRegister = (data, tenantId) => {
  const config = tenantId ? { headers: { 'X-Tenant-ID': tenantId } } : {};
  return API.post("/teacher/invite/verify-otp", data, config);
};

// Constants
export const getTeacherConstants = () =>
  API.get("/teacher/constants");

// ============ Access Request API Start ============

// Public - Submit access request (teacher self-registration)
export const submitAccessRequest = (requestData, tenantId) => {
  const config = tenantId ? { headers: { 'X-Tenant-ID': tenantId } } : {};
  return API.post("/teacher/request-access", requestData, config);
};

// Admin - Get all access requests
export const getAllAccessRequests = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.status) queryParams.append('status', params.status);
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.search) queryParams.append('search', params.search);
  return API.get(`/teacher/access-requests?${queryParams.toString()}`);
};

// Admin - Get access request statistics
export const getAccessRequestStats = () =>
  API.get("/teacher/access-requests/stats");

// Admin - Approve access request
export const approveAccessRequest = (requestId, approvalData) =>
  API.post(`/teacher/access-requests/${requestId}/approve`, approvalData);

// Admin - Reject access request
export const rejectAccessRequest = (requestId, rejectionData) =>
  API.post(`/teacher/access-requests/${requestId}/reject`, rejectionData);

// ============ Access Request API End ============

// ============ Teacher Management API End ============

// ============ Student API Start ============

// Student Auth
export const studentLogin = (data) => API.post('/student/auth/login', data);
export const studentGoogleLogin = (data) => API.post('/student/auth/google-login', data);
export const studentCheckEmail = (data) => API.post('/student/auth/check-email', data);
export const studentForgotPassword = (data) => API.post('/student/auth/forgot-password', data);
export const studentVerifyOtp = (data) => API.post('/student/auth/verify-otp', data);
export const studentResetPassword = (data) => API.post('/student/auth/reset-password', data);
export const getStudentMe = () => API.get('/student/auth/me');

// Student Access Request (Public)
export const submitStudentAccessRequest = (data, tenantId) => {
  const config = tenantId ? { headers: { 'X-Tenant-ID': tenantId } } : {};
  return API.post('/student/request-access', data, config);
};

// Student Access Request Admin (Protected)
export const getStudentAccessRequests = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.status) queryParams.append('status', params.status);
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.search) queryParams.append('search', params.search);
  return API.get(`/student/access-requests?${queryParams.toString()}`);
};
export const getStudentAccessRequestStats = () => API.get('/student/access-requests/stats');
export const getStudentAccessRequestById = (id) => API.get(`/student/access-requests/${id}`);
export const approveStudentAccessRequest = (id, data) => API.post(`/student/access-requests/${id}/approve`, data);
export const rejectStudentAccessRequest = (id, data) => API.post(`/student/access-requests/${id}/reject`, data);

// Student Profile
export const getStudentProfile = () => API.get('/student/profile');
export const updateStudentProfile = (data) => API.patch('/student/profile', data);
export const changeStudentPassword = (data) => API.put('/student/profile/change-password', data);
export const getEnrollmentHistory = () => API.get('/student/profile/enrollment-history');

// Student Progress Analytics
export const getSubjectProgress = () => API.get('/student/progress/subjects');
export const getSubjectDetail = (subject) =>
  API.get(`/student/progress/subjects/${encodeURIComponent(subject)}`);
export const getTopicMastery = (subject = null) => {
  const params = subject ? `?subject=${encodeURIComponent(subject)}` : '';
  return API.get(`/student/progress/topics${params}`);
};
export const getProgressTimeline = (timeRange = '6months') =>
  API.get(`/student/progress/timeline?timeRange=${timeRange}`);
export const getStudyRecommendations = (limit = 5) =>
  API.get(`/student/progress/recommendations?limit=${limit}`);
export const getSelfComparison = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.preset) queryParams.append('preset', params.preset);
  if (params.period1Start) queryParams.append('period1Start', params.period1Start);
  if (params.period1End) queryParams.append('period1End', params.period1End);
  if (params.period2Start) queryParams.append('period2Start', params.period2Start);
  if (params.period2End) queryParams.append('period2End', params.period2End);
  return API.get(`/student/progress/comparison?${queryParams.toString()}`);
};
export const getChapterProgress = (subject) =>
  API.get(`/student/progress/chapters?subject=${encodeURIComponent(subject)}`);
export const downloadProgressReport = (options = {}) => {
  const queryParams = new URLSearchParams();
  if (options.includeSubjects !== undefined) queryParams.append('includeSubjects', options.includeSubjects);
  if (options.includeTopics !== undefined) queryParams.append('includeTopics', options.includeTopics);
  if (options.includeRecommendations !== undefined) queryParams.append('includeRecommendations', options.includeRecommendations);
  if (options.includeTimeline !== undefined) queryParams.append('includeTimeline', options.includeTimeline);
  if (options.format) queryParams.append('format', options.format);
  const responseType = options.format === 'url' ? 'json' : 'blob';
  return API.get(`/student/progress/report/download?${queryParams.toString()}`, { responseType });
};
export const getStudyResources = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.subject) queryParams.append('subject', params.subject);
  if (params.type) queryParams.append('type', params.type);
  if (params.difficulty) queryParams.append('difficulty', params.difficulty);
  if (params.search) queryParams.append('search', params.search);
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  return API.get(`/student/resources?${queryParams.toString()}`);
};
export const getRecommendedResources = () => API.get('/student/resources/recommended');

// Student Dashboard
export const getStudentDashboard = () => API.get('/student/dashboard');
export const getStudentStats = () => API.get('/student/dashboard/stats');
export const getUpcomingAssessments = (days = 7) => API.get(`/student/dashboard/upcoming?days=${days}`);
export const getRecentResults = (limit = 5) => API.get(`/student/dashboard/recent?limit=${limit}`);
export const getPerformanceTrends = (months = 6) => API.get(`/student/dashboard/trends?months=${months}`);

// Student Notifications
export const getStudentNotifications = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.unreadOnly) queryParams.append('unreadOnly', params.unreadOnly);
  return API.get(`/student/notifications?${queryParams.toString()}`);
};
export const getStudentUnreadCount = () => API.get('/student/notifications/unread-count');
export const markNotificationRead = (id) => API.patch(`/student/notifications/${id}/read`);
export const markAllNotificationsRead = () => API.patch('/student/notifications/mark-all-read');
export const cleanupNotifications = (daysOld = 30) => API.delete(`/student/notifications/cleanup?daysOld=${daysOld}`);

// Student Assessments
export const getStudentAssessments = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.subject) queryParams.append('subject', params.subject);
  if (params.status) queryParams.append('status', params.status);
  if (params.search) queryParams.append('search', params.search);
  if (params.sort) queryParams.append('sort', params.sort);
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  return API.get(`/student/assessments?${queryParams.toString()}`);
};
export const getStudentAssessmentById = (id) => API.get(`/student/assessments/${id}`);
export const getStudentAssessmentCalendar = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.month) queryParams.append('month', params.month);
  if (params.year) queryParams.append('year', params.year);
  return API.get(`/student/assessments/calendar?${queryParams.toString()}`);
};
export const getStudentAvailableSubjects = () => API.get('/student/assessments/subjects');
export const getStudentAssessmentSummary = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.startDate) queryParams.append('startDate', params.startDate);
  if (params.endDate) queryParams.append('endDate', params.endDate);
  return API.get(`/student/assessments/summary?${queryParams.toString()}`);
};

// Student Answer Submissions
export const getDraftSubmission = (assessmentId) =>
  API.get(`/student/assessments/${assessmentId}/submission/draft`);

export const uploadSubmissionFile = (assessmentId, formData, captureMethod = 'upload', onProgress) =>
  API.post(
    `/student/assessments/${assessmentId}/submission/upload?captureMethod=${captureMethod}`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        if (onProgress && e.total) {
          onProgress(Math.round((e.loaded * 100) / e.total));
        }
      },
    }
  );

export const reorderSubmissionPages = (submissionId, newOrder) =>
  API.patch(`/student/submissions/${submissionId}/reorder`, { newOrder });

export const deleteSubmissionPage = (submissionId, fileId) =>
  API.delete(`/student/submissions/${submissionId}/files/${fileId}`);

export const submitAnswers = (submissionId) =>
  API.post(`/student/submissions/${submissionId}/submit`);

export const getSubmissionHistory = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.status) queryParams.append('status', params.status);
  return API.get(`/student/submissions?${queryParams.toString()}`);
};

export const getSubmissionById = (submissionId) =>
  API.get(`/student/submissions/${submissionId}`);

// Class-Wise Student Dashboard (Admin)
export const getClassWiseOverview = () =>
  API.get('/student/class-wise');

export const getClassWiseDetail = (classId, params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.section) queryParams.append('section', params.section);
  return API.get(`/student/class-wise/${classId}?${queryParams.toString()}`);
};

export const getNotJoinedStudents = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.classId) queryParams.append('classId', params.classId);
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  return API.get(`/student/class-wise/not-joined?${queryParams.toString()}`);
};

// Bulk Student Upload (Admin)
export const getBulkUploadTemplate = () =>
  API.get('/student/bulk-upload/template');

export const downloadBulkUploadTemplate = () =>
  API.get('/student/bulk-upload/template/download', { responseType: 'blob' });

export const bulkUploadStudents = (students, sendCredentials = false) =>
  API.post('/student/bulk-upload', { students, sendCredentials });

export const addStudent = (student, sendCredentials = false) =>
  API.post('/student/add', { student, sendCredentials });

// Class online assessments (login-gated, assigned to the student's class).
// `useStudentToken` forces the student JWT so a teacher session in the same
// browser doesn't shadow these (the routes require a student token).
export const studentListClassAssessments = () =>
  API.get('/student/online-assessments', { useStudentToken: true });
export const studentGetClassAssessment = (id) =>
  API.get(`/student/online-assessments/${id}`, { useStudentToken: true });
export const studentStartClassAssessment = (id) =>
  API.post(`/student/online-assessments/${id}/start`, {}, { useStudentToken: true });
export const studentGetClassAssessmentResult = (attemptId) =>
  API.get(`/student/online-assessments/attempts/${attemptId}/result`, { useStudentToken: true });

// ============ Student API End ============

// ============ User Profile API Start ============

/**
 * Gets the current user's profile.
 * @returns {Promise} API response with profile data
 */
export const getUserProfile = () => API.get('/user/profile');

/**
 * Updates the current user's profile.
 * @param {Object} profileData - Profile fields to update
 * @returns {Promise} API response with updated profile data
 */
export const updateUserProfile = (profileData) => API.put('/user/profile', profileData);

// ============ User Profile API End ============

// ============ Profile Change Request API Start ============

/**
 * Gets list of restricted fields that require admin approval.
 * @returns {Promise} API response with restricted field names
 */
export const getRestrictedFields = () => API.get('/user/profile/restricted-fields');

/**
 * Creates a profile change request for restricted fields.
 * @param {Object} data - { fieldName, requestedValue, reason }
 * @returns {Promise} API response with created request
 */
export const createProfileChangeRequest = (data) =>
  API.post('/user/profile/change-request', data);

/**
 * Gets current user's pending change requests.
 * @returns {Promise} API response with pending requests
 */
export const getMyPendingChangeRequests = () =>
  API.get('/user/profile/change-requests/pending');

/**
 * Gets current user's change request history.
 * @param {Object} params - { limit, skip }
 * @returns {Promise} API response with request history
 */
export const getMyChangeRequestHistory = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.skip) queryParams.append('skip', params.skip);
  return API.get(`/user/profile/change-requests/history?${queryParams.toString()}`);
};

/**
 * Cancels a pending change request.
 * @param {string} requestId - Request ID to cancel
 * @returns {Promise} API response
 */
export const cancelProfileChangeRequest = (requestId) =>
  API.delete(`/user/profile/change-request/${requestId}`);

// Admin endpoints for profile change requests

/**
 * Gets all pending profile change requests (admin only).
 * @param {Object} params - { limit, skip }
 * @returns {Promise} API response with pending requests
 */
export const getAllPendingProfileChangeRequests = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.skip) queryParams.append('skip', params.skip);
  return API.get(`/user/profile/admin/change-requests?${queryParams.toString()}`);
};

/**
 * Gets profile change request statistics (admin only).
 * @returns {Promise} API response with stats
 */
export const getProfileChangeRequestStats = () =>
  API.get('/user/profile/admin/change-requests/stats');

/**
 * Approves a profile change request (admin only).
 * @param {string} requestId - Request ID
 * @param {string} comment - Optional admin comment
 * @returns {Promise} API response
 */
export const approveProfileChangeRequest = (requestId, comment = '') =>
  API.put(`/user/profile/admin/change-request/${requestId}/approve`, { comment });

/**
 * Rejects a profile change request (admin only).
 * @param {string} requestId - Request ID
 * @param {string} comment - Rejection reason
 * @returns {Promise} API response
 */
export const rejectProfileChangeRequest = (requestId, comment = '') =>
  API.put(`/user/profile/admin/change-request/${requestId}/reject`, { comment });

// ============ Profile Change Request API End ============

// ============ Online Assessment API Start ============

// Teacher endpoints (authenticated)
export const createOnlineAssessment = (data) =>
  API.post('/online-assessment/create', data);

export const listOnlineAssessments = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.status) queryParams.append('status', params.status);
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  return API.get(`/online-assessment/list?${queryParams.toString()}`);
};

export const getOnlineAssessmentDashboard = () =>
  API.get('/online-assessment/dashboard');

export const closeOnlineAssessment = (id) =>
  API.put(`/online-assessment/${id}/close`);

export const getOnlineAssessmentAnalytics = (id) =>
  API.get(`/online-assessment/${id}/analytics`);

export const getOnlineAssessmentParticipants = (id, params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
  return API.get(`/online-assessment/${id}/participants?${queryParams.toString()}`);
};

// One participant's per-question answers (right/wrong drill-down).
export const getOnlineAssessmentParticipantAnswers = (id, attemptId) =>
  API.get(`/online-assessment/${id}/participants/${attemptId}/answers`);

// Class assessment: completion roster + reminders (teacher)
export const getOnlineAssessmentClassCompletion = (id) =>
  API.get(`/online-assessment/${id}/class-completion`);
export const remindOnlineAssessmentClass = (id, studentIds = null) =>
  API.post(`/online-assessment/${id}/remind`, studentIds ? { studentIds } : {});

// Public endpoints (no authentication required)
export const validatePublicAccessCode = (data) =>
  API.post('/online-assessment/public/validate-code', data);

export const validatePublicLinkCode = (data) =>
  API.post('/online-assessment/public/validate-link-code', data);

export const searchPublicInstitutes = (params) =>
  API.get(`/online-assessment/public/institutes?query=${params.query}&limit=${params.limit || 10}`);

export const startPublicQuizAttempt = (data) =>
  API.post('/online-assessment/public/start', data);

// Every call below carries the session token minted at /public/start. The
// attemptId names the row; the token is what says this caller is entitled to it
// (review 2026-09-12 — see utils/quizSession.js).
const withQuizSession = (data) => ({ ...data, sessionToken: quizSessionToken(data.attemptId) });

export const savePublicAnswer = (data) =>
  API.post('/online-assessment/public/save-answer', withQuizSession(data));

export const submitPublicQuiz = (data) =>
  API.post('/online-assessment/public/submit', withQuizSession(data));

export const getPublicQuizResult = (attemptId) =>
  API.get(`/online-assessment/public/result/${attemptId}`, {
    headers: { 'X-Quiz-Session': quizSessionToken(attemptId) || '' },
  });

export const createAccountFromQuiz = (data) =>
  API.post('/online-assessment/public/create-account', data);

// TEST_PREP public endpoints
export const markQuestionForReview = (data) =>
  API.post('/online-assessment/public/mark-review', withQuizSession(data));

export const moveToNextSection = (data) =>
  API.post('/online-assessment/public/move-section', withQuizSession(data));

// Exam config endpoint
export const getExamConfigs = (enabledTypes) => {
  const params = enabledTypes ? `?enabledTypes=${enabledTypes.join(',')}` : '';
  return API.get(`/online-assessment/exam-configs${params}`);
};

// ============ Proctoring (public + teacher) ============
// Batched participant-side event ingest. Rate-limited at the route.
export const recordProctoringEvents = (data) =>
  API.post('/online-assessment/public/proctoring/events', data);

// Auth-gated owner / super-admin read of one attempt's proctoring envelope.
export const getAttemptProctoring = (attemptId) =>
  API.get(`/online-assessment/proctoring/${attemptId}`);

// ============ Online Assessment API End ============

// ============ Diagram Gaps & Rules API Start (Super Admin) ============
export const getDiagramGaps = (params) => API.get('/admin/diagram-gaps', { params });
export const getDiagramGapStats = () => API.get('/admin/diagram-gaps/stats');
export const resolveDiagramGap = (id) => API.put(`/admin/diagram-gaps/${id}/resolve`);
export const reportDiagramGap = (data) => API.post('/assessment/report-diagram', data);
export const uploadDiagramImage = (data) => API.post('/admin/diagram-gaps/upload-image', data);
export const getDiagramRules = () => API.get('/admin/diagram-rules');
export const createDiagramRule = (data) => API.post('/admin/diagram-rules', data);
export const updateDiagramRule = (id, data) => API.put(`/admin/diagram-rules/${id}`, data);
export const deleteDiagramRule = (id) => API.delete(`/admin/diagram-rules/${id}`);
// Two-action review flow (per-gap): generate one candidate, approve it.
// Approve marks candidateStatus=approved + status=resolved; the runtime
// hook in DiagramAssigner then serves the SVG live to future questions.
export const generateGapCandidate = (id) => API.post(`/admin/diagram-gaps/${id}/generate`);
export const approveGapCandidate = (id) => API.post(`/admin/diagram-gaps/${id}/approve`);
// ============ Diagram Gaps & Rules API End ============

// ============ Principal Dashboard API Start ============
export const getPrincipalOverview = (params) => API.get('/principal/overview', { params });
export const getPrincipalTeacherBreakdown = () => API.get('/principal/teachers');
export const getPrincipalTeacherDetail = (teacherId, params) => API.get(`/principal/teachers/${teacherId}`, { params });
export const getPrincipalClassPerformance = (params) => API.get('/principal/analytics/class-performance', { params });
export const getPrincipalSubjectBreakdown = (params) => API.get('/principal/analytics/subject-breakdown', { params });
export const getPrincipalTeacherActivity = (params) => API.get('/principal/analytics/teacher-activity', { params });
// Student analytics — backend scopes by role (admin tenant-wide / teacher own classes).
export const getStudentsAnalytics = () => API.get('/principal/analytics/students');
export const getStudentPerformance = (studentId) => API.get(`/principal/analytics/students/${studentId}/performance`);
// ============ Principal Dashboard API End ============

// ============ Parent Report API Start ============
export const generateParentReportLink = (evaluationId) => API.post('/parent-report/generate', { evaluationId });
export const generateParentReportLinksBulk = (evaluationIds) => API.post('/parent-report/generate-bulk', { evaluationIds });
export const revokeParentReportLink = (token) => API.post('/parent-report/revoke', { token });
export const getParentReport = (token) => API.get(`/parent-report/view/${token}`);
// ============ Parent Report API End ============

// ============ Anonymous Generation Funnel (public, no auth) ============
// PRD-ANONYMOUS-QUESTION-GENERATION — the `/generate` CBSE funnel.
// These endpoints are intentionally PUBLIC: no Bearer/tenant header is
// required (the interceptor simply adds nothing when logged out). We mark the
// generate call with `skipAuthRedirect` so its 401 (GUEST_GEN_LOGIN_REQUIRED —
// the hard 4th-attempt wall) does NOT trip the session-expiry logout.

/**
 * Mints a short-lived signed funnel-entry token (FR-10a). Called on
 * `/generate` page load to prove the request originates from the CBSE funnel.
 * @param {{ class: (string|number), subject: string, chapter?: string }} params
 * @returns {Promise} `{ success, data: { entryToken, expiresInMs } }`
 */
export const getFunnelEntry = ({ class: classNum, subject, chapter } = {}) => {
  const queryParams = new URLSearchParams();
  if (classNum != null && classNum !== '') queryParams.append('class', classNum);
  if (subject) queryParams.append('subject', subject);
  if (chapter) queryParams.append('chapter', chapter);
  return API.get(`/public/funnel-entry?${queryParams.toString()}`);
};

/**
 * Generates 5 CBSE questions (with answers inline) for an anonymous visitor.
 * @param {Object} payload
 * @param {(string|number)} payload.class
 * @param {string} payload.subject
 * @param {string} [payload.chapter]
 * @param {{ mcq?:number, short?:number, long?:number }} [payload.questionTypeMix]
 * @param {string} payload.turnstileToken
 * @param {string} payload.entryToken
 * @returns {Promise} `{ success, data: { guestGenerationId, questions, meta } }`
 */
export const generateAnonymousQuestions = (payload) =>
  API.post('/public/generate-questions', payload, { skipAuthRedirect: true });
// ============ Anonymous Generation Funnel End ============

// UPSC Evaluation API lives in ./upscApi.jsx — split out so the track's
// endpoints ride the lazy UPSC page chunks instead of the landing entry JS
// (keeps the size-limit budget on '/' unaffected). Import from '../api/upscApi'.

// ============ Log Viewer API Start (Super Admin) ============
export const getLogEntries = (params) => API.get('/admin/logs', { params });
export const getLLMUsageStats = (params) => API.get('/admin/logs/llm-usage', { params });
export const getLLMUsageEntries = (params) => API.get('/admin/logs/llm-entries', { params });
export const getLLMCostSummary = (params) => API.get('/admin/logs/llm-cost-summary', { params });
export const getLlmSpend = (params) => API.get('/admin/logs/llm-spend', { params });
export const getLogFiles = () => API.get('/admin/logs/files');
// ============ Log Viewer API End ============

// ============ Prompt Playground API Start (Super Admin) ============
// Layers (CRUD + list)
export const promptLayersList = (params) => API.get('/admin/prompts/layers', { params });
export const promptLayersGet = (layerId) => API.get(`/admin/prompts/layers/${layerId}`);
export const promptLayersCreate = (data) => API.post('/admin/prompts/layers', data);
export const promptLayersUpdate = (layerId, data) => API.put(`/admin/prompts/layers/${layerId}`, data);
export const promptLayersArchive = (layerId) => API.delete(`/admin/prompts/layers/${layerId}`);

// Versions + diff + lifecycle
export const promptLayersVersions = (layerId, params) =>
  API.get(`/admin/prompts/layers/${layerId}/versions`, { params });
export const promptVersionGet = (layerId, versionId) =>
  API.get(`/admin/prompts/layers/${layerId}/versions/${versionId}`);
export const promptVersionPromote = (layerId, versionId, data) =>
  API.post(`/admin/prompts/layers/${layerId}/versions/${versionId}/promote`, data);
export const promptVersionRollback = (layerId, versionId, data) =>
  API.post(`/admin/prompts/layers/${layerId}/versions/${versionId}/rollback`, data);
export const promptVersionDiscard = (layerId, versionId) =>
  API.post(`/admin/prompts/layers/${layerId}/versions/${versionId}/discard`);
export const promptLayerDiff = (layerId, fromId, toId) =>
  API.get(`/admin/prompts/layers/${layerId}/diff`, { params: { from: fromId, to: toId } });

// Assemble (live preview)
export const promptAssemble = (data) => API.post('/admin/prompts/assemble', data);

// Scenario preview — runs real production builders to return the exact
// { systemMessage, userMessage, sourceFile, sourceLine } the runtime sends.
export const promptScenarioPreview = (scenarioId, inputs) =>
  API.post('/admin/prompts/scenarios/preview', { scenarioId, inputs });

// Commits (cross-layer save + revert)
export const promptCommitsList = (params) => API.get('/admin/prompts/commits', { params });
export const promptCommitSave = (data) => API.post('/admin/prompts/commits', data);
export const promptCommitGet = (id) => API.get(`/admin/prompts/commits/${id}`);
export const promptCommitRevert = (id) => API.post(`/admin/prompts/commits/${id}/revert`);

// Import/Export
export const promptLayersExport = () => API.get('/admin/prompts/export');
export const promptLayersImport = (data) => API.post('/admin/prompts/import', data);

// Phase 2A — ephemeral runs
export const promptRun = (body) => API.post('/admin/prompts/runs', body);
export const promptRunsList = ({ layerId, versionId, limit = 20 } = {}) => {
  const qs = new URLSearchParams();
  if (layerId) qs.set('layerId', layerId);
  if (versionId) qs.set('versionId', versionId);
  qs.set('limit', String(limit));
  return API.get(`/admin/prompts/runs?${qs.toString()}`);
};

// Phase 2C — side-by-side compare
export const promptCompare = (body) => API.post('/admin/prompts/compare', body);
// ============ Prompt Playground API End ============

// ============ Admin Access (owner-managed staff) ============
// /me is readable by any super admin; the CRUD endpoints below require
// owner access and will return 403 for non-owner super admins.
export const getAdminAccessMe = () => API.get('/admin/access/me');
export const listAdminStaff = () => API.get('/admin/access/staff');
export const addAdminStaff = (data) => API.post('/admin/access/staff', data);
export const removeAdminStaff = (email) =>
  API.delete(`/admin/access/staff/${encodeURIComponent(email)}`);
// ============ Admin Access End ============

// ============ Log Viewer API End ============

// ============ Suggested Topics (super-admin review queue) ============
// PRD-TOPIC-SUGGESTION-APPROVAL — captures unknown topics teachers type
// during assessment creation, surfaces them here for approval into
// ChaptersTopics.
// Suggested-topics API methods moved to ./suggestedTopicsApi.js (lazy-only) to
// keep them out of this entry-chunk module. Import from '../api/suggestedTopicsApi'.
// ============ Suggested Topics End ============

// V3 Review Queue API methods moved to ./reviewQueueApi.js (lazy-only) to keep
// them out of this entry-chunk module. Import from '../api/reviewQueueApi'.
